import "./i18n";
import "./index.css";

import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { Provider, useSelector } from "react-redux";
import { createBrowserRouter, RouterProvider, useSearchParams } from "react-router-dom";
import ThemeProvider from "./components/ThemeProvider";
import TranslationProvider from "./components/TranslationProvider";
import reportWebVitals from "./reportWebVitals.js";
import AddCard from "./screens/addCard.jsx";
import { CardDetails } from "./screens/cardDetails.jsx";
import Faq from "./screens/faq.jsx";
import FitExtendSelector from "./screens/fitExtendSelector.jsx";
import FitSelector from "./screens/fitSelector.jsx";
import FreefitCibus from "./screens/FreefitCibus.jsx";
import { MapPage } from "./screens/map.jsx";
import MultipleEntryPass from "./screens/multipleEntryPass.jsx";
import MultipleEntryPassDetails from "./screens/multipleEntryPassDetails.jsx";
import MultipleEntryPassPurchase from "./screens/multipleEntryPassPurchase.jsx";
import OrderConfirmation from "./screens/orderConfirmation.jsx";
import OrderHistory from "./screens/orderHistory.jsx";
import OtpValidation from "./screens/otpValidation.jsx";
import PartnerError from "./screens/partnerError.jsx";
import Registration from "./screens/registration.jsx";
import RegistrationQuestionnaire from "./screens/registrationQuestionnaire.jsx";
import SelectDateTime from "./screens/selectDateTime.jsx";
import SelectRoomByDate from "./screens/selectRoomByDate.jsx";
import { Summary } from "./screens/summary.jsx";
import UserPhoneValidation from "./screens/userPhoneValidation.jsx";
import TermsExternal from "./screens/termsExternal.jsx";
import Venue from "./screens/venue.jsx";
import HeaderIconBack from "./shared/Header/HeaderIconBack.jsx";
import HeaderIconBurger from "./shared/Header/HeaderIconBurger.jsx";
import HeaderIconClose from "./shared/Header/HeaderIconClose.jsx";
import HeaderNoLogo from "./shared/Header/HeaderNoLogo.jsx";
import HeaderV2 from "./shared/Header/HeaderV2.jsx";
import LayoutV2 from "./shared/LayoutV2.jsx";
import LegacyHeaderGate from "./v2/components/LegacyHeaderGate.jsx";
import ScreenHeaderGate from "./v2/components/ScreenHeaderGate.jsx";
import { V2_SCREEN_KEYS } from "./v2/registry.js";
import ConnectionError from "./v2/screens/connectionError/ConnectionError.jsx";
import DiscoverPassCard from "./v2/screens/discoverPassCard/DiscoverPassCard.jsx";
import HowItWorks from "./v2/screens/howItWorks/HowItWorks.jsx";
import Profile from "./v2/screens/profile/Profile.jsx";
import AddressSelect from "./v2/screens/addressSelect/AddressSelect.jsx";
import LocationRequired from "./v2/screens/locationRequired/LocationRequired.jsx";
import HomeRoute from "./shared/HomeRoute.jsx";
import Welcome from "./v2/screens/welcome/Welcome.jsx";
import preloadFonts from "./v2/theme/preloadFonts.js";
import { registerRouter } from "./v2/routerRef.js";
import ErrorDialogHost from "./v2/components/ErrorDialogHost.jsx";
import { isPartnerLeumit } from "./store/partnerStore.js";
import store from "./store/rootStore.js";
const TranslatedHeader = ({ headerKey, leumitHeaderKey, ...props }) => {
  const { t } = useTranslation();
  const isLeumit = useSelector(isPartnerLeumit);
  const key = isLeumit && leumitHeaderKey ? leumitHeaderKey : headerKey;
  return <HeaderV2 header={t(key)} {...props} />;
};

const TranslatedHeaderNoLogo = ({ headerKey, ...props }) => {
  const { t } = useTranslation();
  return <HeaderNoLogo header={t(headerKey)} {...props} />;
};

// The /terms-external V2 header title follows ?type=, mirroring how the screen
// body picks the page URL. The legacy header (non-V2 partners) is unchanged.
const TermsExternalScreenHeader = ({ children }) => {
  const [searchParams] = useSearchParams();
  const titleKey = searchParams.get("type") === "privacy" ? "PRIVACY_POLICY" : "TERMS_OF_USE";
  return (
    <ScreenHeaderGate screen={V2_SCREEN_KEYS.TERMS_EXTERNAL} titleKey={titleKey}>
      {children}
    </ScreenHeaderGate>
  );
};

const router = createBrowserRouter(
  [
    {
      // The home page for every partner. Rendered in place (no redirect) so the
      // query string stays on the URL for silentLogin to read the token from.
      path: "/",
      element: (
        <LayoutV2>
          <HomeRoute />
        </LayoutV2>
      ),
    },
    {
      path: "/home",
      element: (
        <LayoutV2>
          <HomeRoute />
        </LayoutV2>
      ),
    },
    {
      // V2-only welcome intro + location gate. Reached only from the end of the
      // first-onboarding chain (OTP → registration → questionnaire) — no other
      // entry navigates here, which is what makes it a one-time screen.
      path: "/welcome",
      element: (
        <LayoutV2>
          <Welcome />
        </LayoutV2>
      ),
    },
    {
      // V2-only profile page (no legacy header — it renders its own top bar).
      path: "/profile",
      element: (
        <LayoutV2>
          <Profile />
        </LayoutV2>
      ),
    },
    {
      // V2-only location gate (no legacy header — it renders its own close control).
      // Reached from the welcome flow when the permission prompt is refused and we have no
      // coordinates on file.
      path: "/location-required",
      element: (
        <LayoutV2>
          <LocationRequired />
        </LayoutV2>
      ),
    },
    {
      // V2-only address-selection page (no legacy header — it renders its own top bar).
      path: "/address-select",
      element: (
        <LayoutV2>
          <AddressSelect />
        </LayoutV2>
      ),
    },
    {
      // V2-only "how it works" explainer (no legacy header — it renders its own ScreenHeader).
      path: "/how-it-works",
      element: (
        <LayoutV2>
          <HowItWorks />
        </LayoutV2>
      ),
    },
    {
      // V2-only pass-card promo (no legacy header — it renders its own X over the hero).
      // Reached from the buy-a-pass promo banners on home, profile, and the purchase summary.
      path: "/discover-pass-card",
      element: (
        <LayoutV2>
          <DiscoverPassCard />
        </LayoutV2>
      ),
    },
    {
      path: "/roomChoice",
      element: (
        <LayoutV2>
          <TranslatedHeader
            headerKey="WHERE_TO_WORKOUT_TODAY"
            leftButton={<HeaderIconBurger />}
            rightButton={<HeaderIconBack />}
          />
          <FitSelector />
        </LayoutV2>
      ),
    },
    {
      path: "/selectDateTime",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.SELECT_DATE_TIME}>
            <TranslatedHeader
              headerKey="WHEN_IS_THE_CLASS"
              leftButton={<HeaderIconClose />}
            />
          </LegacyHeaderGate>
          <SelectDateTime />
        </LayoutV2>
      ),
    },
    {
      path: "/addCard",
      element: (
        <LayoutV2>
          <ScreenHeaderGate screen={V2_SCREEN_KEYS.ADD_CARD} titleKey="CREDIT_CARD_DETAILS">
            <TranslatedHeader
              headerKey="CREDIT_CARD_DETAILS"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </ScreenHeaderGate>
          <AddCard />
        </LayoutV2>
      ),
    },
    {
      path: "/orderConfirmation",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.ORDER_CONFIRMATION}>
            <TranslatedHeader
              headerKey="ORDER_CONFIRMATION"
              leftButton={<HeaderIconClose />}
            />
          </LegacyHeaderGate>
          <OrderConfirmation />
        </LayoutV2>
      ),
    },
    {
      path: "/orderHistory",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.ORDER_HISTORY}>
            <TranslatedHeader
              headerKey="ACTIVITY_ORDER"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </LegacyHeaderGate>
          <OrderHistory />
        </LayoutV2>
      ),
    },
    {
      path: "/fitExtendSelector",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.FIT_EXTEND_SELECTOR}>
            <TranslatedHeader
              headerKey="WHERE_TO_WORKOUT_TODAY"
              leumitHeaderKey="WHERE_TO_GO"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </LegacyHeaderGate>
          <FitExtendSelector />
        </LayoutV2>
      ),
    },
    {
      path: "/multipleEntryPass",
      element: (
        <LayoutV2>
          <ScreenHeaderGate screen={V2_SCREEN_KEYS.MULTIPLE_ENTRY_PASS} titleKey="MY_MULTIPLE_ENTRY_PASS">
            <TranslatedHeader
              headerKey="MY_MULTIPLE_ENTRY_PASS"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </ScreenHeaderGate>
          <MultipleEntryPass />
        </LayoutV2>
      ),
    },
    {
      path: "/multipleEntryPassDetails",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.MULTIPLE_ENTRY_PASS_DETAILS}>
            <TranslatedHeader
              headerKey="MULTIPLE_ENTRY_PASS_DETAILS"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </LegacyHeaderGate>
          <MultipleEntryPassDetails />
        </LayoutV2>
      ),
    },
    {
      path: "/multipleEntryPassPurchase",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.MULTIPLE_ENTRY_PASS_PURCHASE}>
            <TranslatedHeader
              headerKey="PURCHASE_SUMMARY"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </LegacyHeaderGate>
          <MultipleEntryPassPurchase />
        </LayoutV2>
      ),
    },
    {
      path: "/venue/:recordId",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.VENUE}>
            <TranslatedHeader
              headerKey="ACTIVITY_ORDER"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </LegacyHeaderGate>
          <Venue />
        </LayoutV2>
      ),
    },
    {
      path: "/selectRoomByDate",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.SELECT_ROOM_BY_DATE}>
            <TranslatedHeader
              headerKey="ACTIVITY_ORDER"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </LegacyHeaderGate>
          <SelectRoomByDate />
        </LayoutV2>
      ),
    },
    {
      path: "/summary",
      element: (
        <LayoutV2>
          <LegacyHeaderGate screen={V2_SCREEN_KEYS.SUMMARY}>
            <TranslatedHeader
              headerKey="WHERE_TO_WORKOUT_TODAY"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </LegacyHeaderGate>
          <Summary />
        </LayoutV2>
      ),
    },
    {
      path: "/faq",
      element: (
        <LayoutV2>
          <ScreenHeaderGate screen={V2_SCREEN_KEYS.FAQ} titleKey="QUESTIONS_AND_ANSWERS">
            <TranslatedHeader
              headerKey="QUESTIONS_AND_ANSWERS"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </ScreenHeaderGate>
          <Faq />
        </LayoutV2>
      ),
    },
    {
      path: "/registration",
      element: (
        <LayoutV2>
          <Registration />
        </LayoutV2>
      ),
    },
    {
      path: "/registrationQuestionnaire",
      element: (
        <LayoutV2>
          <TranslatedHeader headerKey="HEALTH_DECLARATION_FORM" />
          <RegistrationQuestionnaire />
        </LayoutV2>
      ),
    },
    {
      path: "/map/:latitude/:longitude",
      element: (
        <LayoutV2>
          <ScreenHeaderGate screen={V2_SCREEN_KEYS.MAP} titleKey="MAP">
            <HeaderV2 rightButton={<HeaderIconBack />} />
          </ScreenHeaderGate>
          <MapPage />
        </LayoutV2>
      ),
    },
    {
      path: "/card-details",
      element: (
        <LayoutV2>
          <ScreenHeaderGate screen={V2_SCREEN_KEYS.CARD_DETAILS} titleKey="CREDIT_CARD_DETAILS">
            <TranslatedHeaderNoLogo
              headerKey="CREDIT_CARD_DETAILS"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </ScreenHeaderGate>
          <CardDetails />
        </LayoutV2>
      ),
    },
    {
      path: "/phone-validation",
      element: (
        <LayoutV2>
          <UserPhoneValidation />
        </LayoutV2>
      ),
    },
    {
      path: "/otp-validation",
      element: (
        <LayoutV2>
          <OtpValidation />
        </LayoutV2>
      ),
    },
    {
      path: "/terms-external",
      element: (
        <LayoutV2>
          <TermsExternalScreenHeader>
            <TranslatedHeader
              headerKey="TERMS_OF_USE"
              leftButton={<HeaderIconBurger />}
              rightButton={<HeaderIconBack />}
            />
          </TermsExternalScreenHeader>
          <TermsExternal />
        </LayoutV2>
      ),
    },
    {
      path: "/Cibus",
      element: <FreefitCibus />,
    },
    {
      path: "/partner-error",
      element: <PartnerError />,
    },
    {
      path: "/connection-error",
      element: <ConnectionError />,
    },
  ],
  {
    basename: "/",
  }
);

registerRouter(router);

// Before render: an @font-face file is only fetched once something on the page names
// the family, and nothing does until ThemeProvider injects --v2-font-family. This
// starts the download now instead of after the first paint.
preloadFonts();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <TranslationProvider>
      <ThemeProvider>
        <RouterProvider router={router} />
        {/* App-wide V2 error dialog; sibling of the router so it overlays every route */}
        <ErrorDialogHost />
      </ThemeProvider>
    </TranslationProvider>
  </Provider>
);

reportWebVitals();
